import type { Violencia } from '@/types'

/**
 * The nine forms of violence. Single source of truth for the ring on the home
 * page (SCENES is derived from this list), the /violencias index and every
 * /violencias/[slug] page. Order here is the order around the ring.
 */
export const VIOLENCIAS: Violencia[] = [
    {
        slug: 'fisica',
        nome: "Violência física",
        curto: "Física",
        legenda: "Quando o corpo é atingido",
        resumo: "Qualquer conduta que ofenda a integridade ou a saúde do corpo, deixe ou não marcas visíveis.",
        definicao: [
            "É o uso intencional da força contra o corpo de alguém para ferir, causar dor ou controlar. Tapas, empurrões e puxões de cabelo também são violência, mesmo quando não deixam marcas.",
            "É a forma mais reconhecida, mas raramente aparece sozinha: quase sempre vem acompanhada de ameaças, humilhações e controle. E tende a se repetir e a ficar mais grave com o tempo."
        ],
        lei: {
            texto: "a violência física, entendida como qualquer conduta que ofenda sua integridade ou saúde corporal",
            fonte: "Lei Maria da Penha, Lei 11.340/2006, art. 7º, I"
        },
        exemplos: [
            "Tapas, socos, chutes, empurrões e beliscões",
            "Puxões de cabelo e torções",
            "Apertar o pescoço ou tentar sufocar",
            "Queimaduras e cortes",
            "Arremessar objetos contra a pessoa",
            "Ameaçar ou ferir com faca, arma ou outros objetos"
        ],
        sinais: [
            "Machucados sem explicação, ou com explicações que não combinam com a lesão",
            "Marcas em diferentes estágios de cicatrização",
            "Roupas que escondem o corpo mesmo no calor",
            "Faltas frequentes ao trabalho, à escola ou a compromissos",
            "Medo ou tensão diante do parceiro, da parceira ou de um familiar",
            "Idas repetidas ao pronto-socorro por “acidentes”"
        ],
        agir: [
            {
                titulo: "Em perigo, ligue 190",
                texto: "Se a agressão está acontecendo ou pode acontecer a qualquer momento, chame a Polícia Militar. Vizinhos e testemunhas também podem ligar."
            },
            {
                titulo: "Procure atendimento de saúde",
                texto: "UBS, UPA ou hospital cuidam das lesões, registram o atendimento no prontuário e fazem a notificação. Esse registro ajuda a comprovar a violência depois."
            },
            {
                titulo: "Registre a ocorrência",
                texto: "Em uma Delegacia da Mulher ou em qualquer delegacia. O exame de corpo de delito documenta as lesões."
            },
            {
                titulo: "Peça medida protetiva",
                texto: "A Justiça pode afastar quem agride de casa e proibir que se aproxime ou faça contato. O pedido pode ser feito na delegacia, na Defensoria Pública ou no Ministério Público."
            }
        ],
        canais: ['policia', 'mulher', 'direitos', 'samu'],
        marcos: [
            {
                nome: "Lei 11.340/2006, Lei Maria da Penha",
                texto: "Define a violência física como uma das formas de violência doméstica e familiar contra a mulher e cria as medidas protetivas de urgência."
            },
            {
                nome: "Código Penal, art. 129",
                texto: "Lesão corporal. Quando praticada contra a mulher por razões da condição do sexo feminino, a pena é maior."
            },
            {
                nome: "Lei 14.994/2024",
                texto: "Tornou o feminicídio um crime autônomo, com pena de 20 a 40 anos de reclusão."
            }
        ],
        atencao: {
            titulo: "Sinais de risco grave",
            texto: "Tentativas de esganadura ou sufocamento, ameaças com arma e agressões cada vez mais frequentes indicam risco de feminicídio. Nesses casos, procure ajuda imediatamente."
        },
        cena: {
            image: 'fisica',
            bgFocus: '50% 50%',
            thumbFocus: '50% 55%',
            thumbScale: 1.35,
            tone: 'paint',
            accent: '#e8702f'
        }
    },
    {
        slug: 'psicologica',
        nome: "Violência psicológica",
        curto: "Psicológica",
        legenda: "A ferida que não aparece",
        resumo: "Humilhar, ameaçar, controlar e isolar para diminuir a autoestima e dominar as decisões de outra pessoa.",
        definicao: [
            "É toda conduta que causa dano emocional, diminui a autoestima ou busca controlar as ações, os comportamentos, as crenças e as decisões de alguém. Não deixa marcas no corpo, mas adoece: ansiedade, depressão, insônia e pensamentos de morte são consequências frequentes.",
            "Por ser silenciosa, costuma ser a primeira a aparecer e a última a ser reconhecida. Muitas vezes quem sofre passa a duvidar da própria percepção, porque é levado a acreditar que exagera ou que tem culpa."
        ],
        lei: {
            texto: "qualquer conduta que lhe cause dano emocional e diminuição da autoestima ou que lhe prejudique e perturbe o pleno desenvolvimento ou que vise degradar ou controlar suas ações, comportamentos, crenças e decisões",
            fonte: "Lei Maria da Penha, Lei 11.340/2006, art. 7º, II"
        },
        exemplos: [
            "Humilhar, xingar e ridicularizar, em público ou em particular",
            "Ameaçar de agressão, de morte ou de tirar os filhos",
            "Controlar roupas, horários, amizades, celular e redes sociais",
            "Isolar da família e dos amigos",
            "Vigiar e perseguir de forma insistente",
            "Fazer a pessoa duvidar da própria memória e do próprio juízo",
            "Chantagear e manipular com culpa ou ciúme"
        ],
        sinais: [
            "Medo constante de desagradar, como quem “pisa em ovos”",
            "Afastamento dos amigos, da família e de atividades de que gostava",
            "Tristeza, ansiedade, insônia e queda na autoestima",
            "Pedir permissão para decisões simples do dia a dia",
            "Justificar o comportamento de quem agride",
            "Sentir culpa por tudo o que dá errado"
        ],
        agir: [
            {
                titulo: "Nomeie o que está acontecendo",
                texto: "Reconhecer que humilhação, controle e ameaça são violência é o primeiro passo. Não é exagero e não é culpa de quem sofre."
            },
            {
                titulo: "Guarde registros",
                texto: "Mensagens, áudios, e-mails e anotações com datas ajudam a comprovar a violência psicológica e a pedir proteção."
            },
            {
                titulo: "Busque apoio",
                texto: "Converse com alguém de confiança e procure uma unidade de saúde, um CAPS ou um Centro de Referência da Mulher. O Ligue 180 orienta a qualquer hora."
            },
            {
                titulo: "Peça medida protetiva",
                texto: "A violência psicológica também justifica medidas protetivas de urgência, mesmo sem nenhuma agressão física."
            }
        ],
        canais: ['mulher', 'cvv', 'direitos', 'policia'],
        marcos: [
            {
                nome: "Lei 11.340/2006, art. 7º, II",
                texto: "Reconhece a violência psicológica como forma de violência doméstica e familiar contra a mulher."
            },
            {
                nome: "Código Penal, art. 147-B",
                texto: "Crime de violência psicológica contra a mulher, incluído pela Lei 14.188/2021."
            },
            {
                nome: "Código Penal, art. 147-A",
                texto: "Crime de perseguição, o chamado stalking, incluído pela Lei 14.132/2021."
            },
            {
                nome: "Código Penal, art. 147",
                texto: "Crime de ameaça."
            }
        ],
        cena: {
            image: 'psicologica',
            bgFocus: '50% 50%',
            thumbFocus: '50% 48%',
            thumbScale: 1.3,
            tone: 'paint',
            accent: '#a9b6a0'
        }
    },
    {
        slug: 'sexual',
        nome: "Violência sexual",
        curto: "Sexual",
        legenda: "Sem consentimento, é violência",
        resumo: "Qualquer ato sexual sem consentimento, obtido por força, ameaça, coação ou aproveitando-se de quem não pode consentir.",
        definicao: [
            "É obrigar alguém, por força física, ameaça, intimidação ou manipulação, a presenciar, manter ou participar de ato sexual que não deseja. Também é violência sexual impedir o uso de métodos contraceptivos ou forçar gravidez, aborto, casamento ou prostituição.",
            "Acontece também dentro do casamento e do namoro, e muitas vezes é praticada por pessoas conhecidas e de confiança. Com menores de 14 anos, qualquer ato sexual é crime, independentemente de um suposto consentimento."
        ],
        lei: {
            texto: "qualquer conduta que a constranja a presenciar, a manter ou a participar de relação sexual não desejada, mediante intimidação, ameaça, coação ou uso da força",
            fonte: "Lei Maria da Penha, Lei 11.340/2006, art. 7º, III"
        },
        exemplos: [
            "Estupro, inclusive pelo marido, companheiro ou namorado",
            "Toques, beijos e outros atos sexuais sem consentimento",
            "Importunação sexual em ônibus, ruas e festas",
            "Assédio sexual no trabalho ou na escola",
            "Abuso e exploração sexual de crianças e adolescentes",
            "Impedir o uso de camisinha ou de outro método contraceptivo",
            "Gravar ou divulgar imagens íntimas sem autorização"
        ],
        sinais: [
            "Mudanças bruscas de comportamento, medo ou isolamento",
            "Lesões, dor ou sangramento na região genital",
            "Infecções sexualmente transmissíveis ou gravidez inesperada",
            "Em crianças, comportamentos e conhecimentos sexuais incompatíveis com a idade",
            "Pesadelos, medo de ficar a sós com alguém, recusa em ir a determinados lugares"
        ],
        agir: [
            {
                titulo: "Procure um serviço de saúde o quanto antes",
                texto: "De preferência nas primeiras 72 horas: há medicamentos que previnem o HIV e outras infecções, e a contracepção de emergência evita a gravidez. O atendimento imediato é um direito garantido pela Lei 12.845/2013."
            },
            {
                titulo: "O cuidado não depende de boletim de ocorrência",
                texto: "Você pode ser atendida ou atendido sem registrar ocorrência. Registrar é um direito, não uma condição para o cuidado."
            },
            {
                titulo: "Se puder, preserve vestígios",
                texto: "Evitar o banho e guardar as roupas em saco de papel ajuda a perícia. Se isso não for possível, procure atendimento mesmo assim."
            },
            {
                titulo: "Com crianças e adolescentes",
                texto: "Acolha, acredite e não faça a criança repetir o relato. Comunique ao Conselho Tutelar ou ao Disque 100."
            }
        ],
        canais: ['policia', 'mulher', 'direitos', 'samu'],
        marcos: [
            {
                nome: "Lei 12.845/2013, Lei do Minuto Seguinte",
                texto: "Garante atendimento imediato, obrigatório e integral nos hospitais do SUS a pessoas em situação de violência sexual."
            },
            {
                nome: "Código Penal, arts. 213 e 217-A",
                texto: "Estupro e estupro de vulnerável, que inclui qualquer ato sexual com menores de 14 anos."
            },
            {
                nome: "Código Penal, art. 215-A",
                texto: "Importunação sexual, crime incluído pela Lei 13.718/2018."
            },
            {
                nome: "Código Penal, art. 128, II",
                texto: "Permite a interrupção da gestação resultante de estupro, que pode ser realizada pelo SUS."
            }
        ],
        atencao: {
            titulo: "As primeiras 72 horas importam",
            texto: "A prevenção contra o HIV deve começar o quanto antes, idealmente nas primeiras horas e no máximo em 72 horas. Procure a unidade de saúde mais próxima ou ligue 192."
        },
        cena: {
            image: 'sexual',
            bgFocus: '50% 50%',
            thumbFocus: '50% 50%',
            thumbScale: 1.3,
            tone: 'paint',
            accent: '#e0903a'
        }
    },
    {
        slug: 'moral',
        nome: "Violência moral",
        curto: "Moral",
        legenda: "Quando a palavra fere a honra",
        resumo: "Calúnia, difamação e injúria: atacar a reputação e a dignidade de alguém com acusações, mentiras e ofensas.",
        definicao: [
            "É toda conduta que ofende a honra e a reputação de uma pessoa. Acusar falsamente de um crime, espalhar histórias para destruir a imagem de alguém ou xingar para rebaixar são formas de violência moral.",
            "Hoje ela acontece muito nas redes sociais e nos grupos de mensagens, onde a exposição se multiplica. Quando a ofensa usa raça, cor, etnia ou origem, pode configurar injúria racial, crime equiparado ao racismo."
        ],
        lei: {
            texto: "a violência moral, entendida como qualquer conduta que configure calúnia, difamação ou injúria",
            fonte: "Lei Maria da Penha, Lei 11.340/2006, art. 7º, V"
        },
        exemplos: [
            "Acusar falsamente de ter cometido um crime",
            "Espalhar mentiras ou fatos íntimos para destruir a reputação",
            "Xingar e ofender a dignidade, em público ou em particular",
            "Expor ao ridículo diante da família, de colegas ou nas redes",
            "Desqualificar como mãe, pai, profissional ou pessoa",
            "Ofensas racistas, misóginas, LGBTfóbicas ou capacitistas"
        ],
        sinais: [
            "Vergonha de sair, de trabalhar ou de encontrar pessoas",
            "Isolamento depois de uma exposição pública ou nas redes",
            "Medo do que o outro pode dizer ou publicar",
            "Queda na autoestima e na confiança"
        ],
        agir: [
            {
                titulo: "Guarde provas",
                texto: "Capturas de tela com data e endereço da publicação, áudios, mensagens e nomes de testemunhas."
            },
            {
                titulo: "Registre a ocorrência",
                texto: "Calúnia, difamação e injúria são crimes. Na maioria dos casos, o processo depende de queixa apresentada por advogado ou pela Defensoria Pública em até seis meses."
            },
            {
                titulo: "Peça a retirada do conteúdo",
                texto: "Denuncie a publicação na própria plataforma. Se a exposição continuar, procure a Defensoria Pública."
            },
            {
                titulo: "Quando há relação doméstica",
                texto: "Se quem ofende é parceiro, ex ou familiar, a violência moral também é coberta pela Lei Maria da Penha e pode justificar medidas protetivas."
            }
        ],
        canais: ['mulher', 'direitos', 'policia'],
        marcos: [
            {
                nome: "Código Penal, arts. 138, 139 e 140",
                texto: "Calúnia, difamação e injúria."
            },
            {
                nome: "Lei 14.532/2023",
                texto: "Equiparou a injúria racial ao crime de racismo."
            },
            {
                nome: "Lei 11.340/2006, art. 7º, V",
                texto: "Reconhece a violência moral como forma de violência doméstica e familiar contra a mulher."
            }
        ],
        cena: {
            image: 'moral',
            bgFocus: '50% 45%',
            thumbFocus: '50% 42%',
            thumbScale: 1.3,
            tone: 'paint',
            accent: '#cdbf86'
        }
    },
    {
        slug: 'patrimonial',
        nome: "Violência patrimonial",
        curto: "Patrimonial",
        legenda: "Controle pelo dinheiro e pelos bens",
        resumo: "Reter, destruir ou tomar documentos, objetos, dinheiro e bens para controlar e tirar a autonomia de alguém.",
        definicao: [
            "É controlar a vida de alguém por meio do dinheiro e das coisas: reter documentos, destruir objetos, tomar o salário ou impedir que a pessoa trabalhe. Ao tirar a autonomia financeira, quem agride torna mais difícil romper o ciclo da violência.",
            "Atinge com frequência mulheres e pessoas idosas, que podem ter benefícios, cartões e aposentadorias tomados por familiares."
        ],
        lei: {
            texto: "qualquer conduta que configure retenção, subtração, destruição parcial ou total de seus objetos, instrumentos de trabalho, documentos pessoais, bens, valores e direitos ou recursos econômicos",
            fonte: "Lei Maria da Penha, Lei 11.340/2006, art. 7º, IV"
        },
        exemplos: [
            "Reter documentos pessoais, cartões ou celular",
            "Controlar o salário, o benefício ou a aposentadoria",
            "Quebrar objetos, roupas e instrumentos de trabalho",
            "Impedir de trabalhar ou de estudar",
            "Fazer dívidas ou empréstimos em nome da outra pessoa",
            "Vender ou esconder bens do casal sem consentimento"
        ],
        sinais: [
            "Não ter acesso ao próprio dinheiro, cartão ou documentos",
            "Precisar pedir ou justificar cada gasto",
            "Objetos pessoais destruídos depois de discussões",
            "Dívidas desconhecidas em seu nome",
            "Em pessoas idosas, benefícios que “somem” e contas sem explicação"
        ],
        agir: [
            {
                titulo: "Guarde cópias de documentos",
                texto: "Fotos de RG, CPF, certidões, cartão do SUS e comprovantes, em um lugar seguro ou com alguém de confiança."
            },
            {
                titulo: "Procure a Defensoria Pública",
                texto: "Ela orienta sobre divisão de bens, pensão alimentícia e devolução de documentos e objetos."
            },
            {
                titulo: "Peça medidas protetivas",
                texto: "A Lei Maria da Penha prevê medidas para proteger o patrimônio da mulher, como a devolução de bens tomados indevidamente."
            },
            {
                titulo: "Com pessoas idosas",
                texto: "Apropriar-se de benefícios e rendimentos da pessoa idosa é crime. Denuncie pelo Disque 100."
            }
        ],
        canais: ['mulher', 'direitos', 'policia'],
        marcos: [
            {
                nome: "Lei 11.340/2006, arts. 7º, IV, e 24",
                texto: "Define a violência patrimonial e prevê medidas para proteger os bens da mulher, como a restituição do que foi tomado."
            },
            {
                nome: "Lei 10.741/2003, Estatuto da Pessoa Idosa",
                texto: "Pune quem se apropria de bens, proventos, pensão ou benefícios da pessoa idosa."
            }
        ],
        cena: {
            image: 'patrimonial',
            bgFocus: '50% 55%',
            thumbFocus: '53% 62%',
            thumbScale: 1.35,
            tone: 'paint',
            accent: '#9bcf6a'
        }
    },
    {
        slug: 'negligencia',
        nome: "Negligência e abandono",
        curto: "Negligência",
        legenda: "A ausência do cuidado devido",
        resumo: "Deixar de oferecer os cuidados básicos a quem depende de outra pessoa: alimentação, higiene, saúde, proteção e afeto.",
        definicao: [
            "É a omissão de quem tem o dever de cuidar. Atinge principalmente crianças, adolescentes, pessoas idosas, pessoas com deficiência e pessoas acamadas ou doentes que dependem de terceiros. O abandono é a sua forma mais extrema.",
            "Nem toda falta de cuidado é negligência: muitas famílias em situação de pobreza não têm como garantir o básico. Por isso, o olhar da rede deve ser de proteção e apoio, e não apenas de punição."
        ],
        exemplos: [
            "Deixar de alimentar, de cuidar da higiene ou de dar remédios",
            "Não levar a serviços de saúde ou interromper tratamentos",
            "Deixar crianças pequenas sozinhas ou sem supervisão",
            "Não matricular ou não garantir a frequência à escola",
            "Abandonar pessoa idosa em hospital, instituição ou em casa",
            "Expor a riscos, como frio, calor, fome ou ambiente insalubre"
        ],
        sinais: [
            "Desnutrição, desidratação ou perda de peso sem explicação",
            "Higiene precária persistente e roupas inadequadas ao clima",
            "Feridas, assaduras ou lesões por pressão sem tratamento",
            "Faltas frequentes à escola e vacinas atrasadas",
            "Pessoa idosa ou com deficiência sozinha por longos períodos"
        ],
        agir: [
            {
                titulo: "Acolha antes de julgar",
                texto: "Entenda a situação da família. Muitas vezes o que falta é apoio da rede, e não vontade de cuidar."
            },
            {
                titulo: "Acione a rede de proteção",
                texto: "Para crianças e adolescentes, o Conselho Tutelar. Para pessoas idosas e pessoas com deficiência, o CREAS, o Ministério Público ou o Disque 100."
            },
            {
                titulo: "Profissionais, notifiquem",
                texto: "Casos suspeitos e confirmados devem ser notificados pelos serviços de saúde e comunicados aos órgãos de proteção."
            },
            {
                titulo: "Em risco imediato",
                texto: "Quando houver perigo à vida, ligue 190 ou 192."
            }
        ],
        canais: ['direitos', 'samu', 'policia'],
        marcos: [
            {
                nome: "Lei 8.069/1990, Estatuto da Criança e do Adolescente",
                texto: "Proíbe qualquer forma de negligência e obriga a comunicar ao Conselho Tutelar os casos suspeitos ou confirmados de maus-tratos."
            },
            {
                nome: "Lei 10.741/2003, Estatuto da Pessoa Idosa",
                texto: "Pune o abandono e prevê a notificação compulsória da violência contra a pessoa idosa."
            },
            {
                nome: "Lei 13.146/2015, Estatuto da Pessoa com Deficiência",
                texto: "Prevê a notificação compulsória da violência contra a pessoa com deficiência."
            },
            {
                nome: "Código Penal, arts. 133, 136 e 244",
                texto: "Abandono de incapaz, maus-tratos e abandono material."
            }
        ],
        cena: {
            image: 'negligencia',
            bgFocus: '50% 50%',
            thumbFocus: '52% 60%',
            thumbScale: 1.3,
            tone: 'paint',
            accent: '#cda86a'
        }
    },
    {
        slug: 'institucional',
        nome: "Violência institucional",
        curto: "Institucional",
        legenda: "Quando quem deveria proteger fere",
        resumo: "A violência praticada por serviços e instituições, por ação ou omissão, inclusive quando revitimiza quem busca ajuda.",
        definicao: [
            "Acontece dentro de serviços públicos e privados, como unidades de saúde, delegacias, escolas, abrigos e o sistema de justiça. Pode ser a demora, o descaso, a discriminação, a culpabilização da vítima ou procedimentos desnecessários que fazem a pessoa reviver o sofrimento.",
            "Tem relação direta com o racismo, o machismo, a LGBTfobia e o preconceito de classe que atravessam as instituições. Quando a pessoa precisa percorrer vários serviços sem resposta, essa peregrinação também é uma forma de violência."
        ],
        exemplos: [
            "Desacreditar ou culpar a vítima no atendimento",
            "Obrigar a contar a violência várias vezes, sem necessidade",
            "Negar, atrasar ou dificultar um atendimento a que a pessoa tem direito",
            "Tratamento discriminatório por raça, gênero, orientação sexual, idade, deficiência ou classe",
            "Violência obstétrica, como procedimentos sem consentimento e humilhações no parto",
            "Expor a intimidade e não garantir privacidade no atendimento"
        ],
        sinais: [
            "Sair do serviço pior do que entrou, com sensação de humilhação ou culpa",
            "Ser encaminhado de um lugar a outro sem solução",
            "Ter direitos negados sem explicação",
            "Desistir de buscar ajuda depois de um mau atendimento"
        ],
        agir: [
            {
                titulo: "Registre o que aconteceu",
                texto: "Anote data, horário, local, nomes e o que foi dito. Peça cópia de documentos e do prontuário."
            },
            {
                titulo: "Procure a ouvidoria",
                texto: "Toda instituição pública tem ouvidoria. No SUS, a reclamação também pode ser feita pelo Disque Saúde 136."
            },
            {
                titulo: "Acione a Defensoria ou o Ministério Público",
                texto: "Eles podem orientar e agir para garantir o atendimento e responsabilizar a instituição."
            },
            {
                titulo: "Profissionais, cuidem do acolhimento",
                texto: "Escuta sem julgamento, privacidade, informação clara e um único relato bem registrado evitam a revitimização."
            }
        ],
        canais: ['saude', 'direitos', 'mulher'],
        marcos: [
            {
                nome: "Lei 14.321/2022",
                texto: "Tornou crime a violência institucional do agente público que submete vítimas ou testemunhas a procedimentos desnecessários, repetitivos ou invasivos."
            },
            {
                nome: "Lei 13.431/2017",
                texto: "Criou a escuta especializada e o depoimento especial, para que crianças e adolescentes não sejam revitimizados."
            },
            {
                nome: "Lei 14.245/2021, Lei Mariana Ferrer",
                texto: "Protege a dignidade de vítimas e testemunhas durante audiências e julgamentos."
            }
        ],
        cena: {
            image: 'institucional',
            bgFocus: '50% 50%',
            thumbFocus: '50% 50%',
            thumbScale: 1.25,
            tone: 'paint',
            accent: '#cf9ab0'
        }
    },
    {
        slug: 'digital',
        nome: "Violência digital",
        curto: "Digital",
        legenda: "A agressão que atravessa a tela",
        resumo: "Perseguir, expor, ameaçar e controlar por meio do celular, das redes sociais e da internet.",
        definicao: [
            "São violências praticadas com o uso de tecnologias: vigiar o celular, rastrear a localização, invadir contas, divulgar imagens íntimas, criar perfis falsos, ameaçar e humilhar nas redes. O ambiente é virtual, mas os danos são reais e muitas vezes acompanham outras formas de violência.",
            "Atinge pessoas de todas as idades. Crianças e adolescentes estão especialmente expostos ao cyberbullying, ao aliciamento e à exploração sexual on-line."
        ],
        exemplos: [
            "Exigir senhas e vigiar mensagens, fotos e localização",
            "Instalar aplicativos espiões no celular de outra pessoa",
            "Divulgar ou ameaçar divulgar fotos e vídeos íntimos",
            "Criar montagens e vídeos falsos de conteúdo sexual",
            "Perseguição, ameaças e ataques coordenados nas redes",
            "Cyberbullying entre crianças e adolescentes",
            "Aliciamento de crianças e adolescentes pela internet"
        ],
        sinais: [
            "Medo ou ansiedade ao receber notificações",
            "Afastamento repentino das redes ou uso escondido do celular",
            "Alguém sabe de conversas e lugares que não foram contados a ninguém",
            "Contas acessadas por outras pessoas e aparelhos desconhecidos conectados",
            "Em crianças e adolescentes, segredos com “amigos” virtuais"
        ],
        agir: [
            {
                titulo: "Preserve as provas",
                texto: "Faça capturas de tela com data, hora, endereço da página e nome do perfil. Uma ata notarial feita em cartório dá ainda mais força à prova."
            },
            {
                titulo: "Denuncie na plataforma",
                texto: "Redes sociais e aplicativos devem retirar imagens íntimas divulgadas sem autorização depois de notificados pela vítima."
            },
            {
                titulo: "Proteja suas contas",
                texto: "Troque senhas, ative a verificação em duas etapas, revise os aparelhos conectados e desative o compartilhamento de localização."
            },
            {
                titulo: "Registre a ocorrência",
                texto: "Em delegacias de crimes cibernéticos ou em qualquer delegacia. Com crianças e adolescentes, acione também o Conselho Tutelar ou o Disque 100."
            }
        ],
        canais: ['mulher', 'direitos', 'policia'],
        marcos: [
            {
                nome: "Lei 12.965/2014, Marco Civil da Internet, art. 21",
                texto: "Responsabiliza as plataformas que não retiram imagens íntimas divulgadas sem autorização depois da notificação da vítima."
            },
            {
                nome: "Lei 13.718/2018",
                texto: "Tornou crime divulgar cena de sexo, nudez ou estupro sem consentimento."
            },
            {
                nome: "Lei 12.737/2012",
                texto: "Tornou crime invadir dispositivo informático de outra pessoa."
            },
            {
                nome: "Leis 14.132/2021 e 14.811/2024",
                texto: "Criminalizaram a perseguição, inclusive pela internet, e a intimidação sistemática virtual, o cyberbullying."
            }
        ],
        cena: {
            image: 'digital',
            bgFocus: '50% 55%',
            thumbFocus: '50% 55%',
            thumbScale: 1.3,
            tone: 'paint',
            accent: '#7fa6cf'
        }
    },
    {
        slug: 'autoprovocada',
        nome: "Violência autoprovocada",
        curto: "Autoprovocada",
        legenda: "Quando a dor se volta para si",
        resumo: "Autolesões, tentativas de suicídio e pensamentos de tirar a própria vida. Pedir ajuda é possível, e falar sobre isso salva vidas.",
        definicao: [
            "Inclui a ideação suicida, a autolesão, as tentativas de suicídio e o suicídio. Resulta de muitos fatores ao mesmo tempo, como sofrimento psíquico, violências vividas, perdas, isolamento e falta de apoio.",
            "Não é fraqueza nem busca de atenção: é sinal de um sofrimento intenso que precisa de cuidado. Perguntar com respeito sobre pensamentos de morte não induz ninguém ao suicídio. Ao contrário, abre espaço para a ajuda."
        ],
        exemplos: [
            "Pensamentos frequentes sobre morrer ou desaparecer",
            "Ferir o próprio corpo para aliviar a dor emocional",
            "Planos ou tentativas de tirar a própria vida",
            "Comportamentos de risco repetidos, sem cuidado com a própria segurança"
        ],
        sinais: [
            "Falar em morrer, em não ter saída ou em ser um peso para os outros",
            "Isolamento, despedidas e doação de objetos de valor",
            "Mudanças bruscas de humor, inclusive uma calma repentina depois de uma crise",
            "Aumento do uso de álcool ou de outras drogas",
            "Ferimentos escondidos e roupas que cobrem braços e pernas o tempo todo"
        ],
        agir: [
            {
                titulo: "Pergunte e escute sem julgar",
                texto: "Pergunte diretamente se a pessoa está pensando em se machucar. Ouça com calma, sem sermão e sem minimizar o que ela sente."
            },
            {
                titulo: "Não deixe a pessoa sozinha em risco",
                texto: "Se o risco for imediato, fique com ela, afaste objetos que possam causar dano e ligue 192."
            },
            {
                titulo: "Procure cuidado",
                texto: "Unidades de saúde e CAPS atendem crises e acompanham o tratamento. O CVV, no 188, oferece apoio emocional a qualquer hora."
            },
            {
                titulo: "Se é você quem está sofrendo",
                texto: "Você não precisa passar por isso sem ajuda. Ligue 188, procure alguém de confiança ou um serviço de saúde hoje."
            }
        ],
        canais: ['cvv', 'samu'],
        marcos: [
            {
                nome: "Lei 13.819/2019",
                texto: "Instituiu a Política Nacional de Prevenção da Automutilação e do Suicídio e a notificação compulsória da violência autoprovocada."
            },
            {
                nome: "Notificação no SUS",
                texto: "Tentativas de suicídio são de notificação imediata pelos serviços de saúde, para garantir cuidado e acompanhamento."
            }
        ],
        atencao: {
            titulo: "Se você pensa em tirar a própria vida",
            texto: "Ligue 188 agora, de graça e a qualquer hora, ou procure o serviço de saúde mais próximo. Em risco imediato, ligue 192."
        },
        cena: {
            image: 'autoprovocada',
            bgFocus: '50% 55%',
            thumbFocus: '50% 58%',
            thumbScale: 1.3,
            tone: 'paint',
            accent: '#d98a7a'
        }
    }
]

export const violenciaPorSlug = (slug: string): Violencia | undefined => {
    return VIOLENCIAS.find(item => item.slug === slug)
}
